/**
 * @fileoverview
 * Navigation Prevention Service - Prevents browser navigation (back/forward buttons, page refresh, etc.)
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal, effect } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationStart, NavigationCancel } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class NavigationPreventionService {
  private location = inject(Location);
  private router = inject(Router);
  
  private isEnabledSignal = signal<boolean>(false);
  private preventPageUnloadSignal = signal<boolean>(false);
  
  private popStateListener?: (event: PopStateEvent) => void;
  private beforeUnloadListener?: (event: BeforeUnloadEvent) => void;
  private hashChangeListener?: () => void;
  private navigationStartSubscription?: any;
  private navigationEndSubscription?: any;
  private currentUrl: string = '';

  // Public readonly signals
  readonly isEnabled = this.isEnabledSignal.asReadonly();
  readonly preventPageUnload = this.preventPageUnloadSignal.asReadonly();

  constructor() {
    // Track current URL
    this.currentUrl = this.location.path();
    
    // Effect to handle URL changes when enabled
    effect(() => {
      if (this.isEnabledSignal()) {
        // Store current URL when enabled
        this.currentUrl = this.location.path();
      }
    });
  }

  /**
   * Enable navigation prevention
   * @param preventPageUnload - If true, also prevents page refresh/close
   */
  enable(preventPageUnload: boolean = false): void {
    if (this.isEnabledSignal()) {
      console.log('⚠️ Navigation prevention already enabled');
      return; // Already enabled
    }

    console.log('🔒 Enabling navigation prevention...');
    this.isEnabledSignal.set(true);
    this.preventPageUnloadSignal.set(preventPageUnload);

    // Prevent browser back/forward buttons
    this.setupPopStatePrevention();

    // Prevent page refresh/close if requested
    if (preventPageUnload) {
      this.setupBeforeUnloadPrevention();
    }

    // Prevent programmatic navigation
    this.setupRouterNavigationPrevention();
    
    console.log('✅ Navigation prevention enabled');
  }

  /**
   * Disable navigation prevention
   */
  disable(): void {
    if (!this.isEnabledSignal()) {
      return; // Already disabled
    }

    this.isEnabledSignal.set(false);
    this.preventPageUnloadSignal.set(false);

    // Remove event listeners
    this.removePopStatePrevention();
    this.removeBeforeUnloadPrevention();
    this.removeRouterNavigationPrevention();
  }

  /**
   * Setup prevention for browser back/forward buttons
   */
  private setupPopStatePrevention(): void {
    // Store current URL
    this.currentUrl = this.location.path() || '/';
    console.log('🔒 Navigation prevention: Current URL set to', this.currentUrl);
    
    // Push a state to history to prevent back navigation
    history.pushState(null, '', window.location.href);

    // Listen for popstate events (back/forward button clicks)
    this.popStateListener = (event: PopStateEvent) => {
      console.log('🚫 Popstate event detected - preventing navigation');
      
      // Immediately push state again to prevent navigation
      history.pushState(null, '', window.location.href);
      
      // Use Angular's Location service to restore the current URL synchronously
      this.location.replaceState(this.currentUrl);
      
      // Force Angular router to stay on current route
      // Use requestAnimationFrame to ensure it happens in the next frame
      requestAnimationFrame(() => {
        this.router.navigateByUrl(this.currentUrl, { 
          skipLocationChange: false,
          replaceUrl: true 
        }).then(() => {
          console.log('✅ Navigation prevented, stayed on', this.currentUrl);
        }).catch(() => {
          // If navigation fails, ensure URL is correct
          this.location.replaceState(this.currentUrl);
          console.log('⚠️ Navigation prevention fallback applied');
        });
      });
    };

    // Use capture phase to intercept before Angular processes it
    window.addEventListener('popstate', this.popStateListener, true);
    console.log('👂 Popstate listener registered');
    
    // Also intercept hashchange for hash-based routing
    this.hashChangeListener = () => {
      if (this.isEnabledSignal()) {
        console.log('🚫 Hashchange detected - preventing');
        history.pushState(null, '', window.location.href);
        this.location.replaceState(this.currentUrl);
      }
    };
    window.addEventListener('hashchange', this.hashChangeListener, true);
  }

  /**
   * Remove popstate prevention
   */
  private removePopStatePrevention(): void {
    if (this.popStateListener) {
      window.removeEventListener('popstate', this.popStateListener, true);
      this.popStateListener = undefined;
    }
    if (this.hashChangeListener) {
      window.removeEventListener('hashchange', this.hashChangeListener, true);
      this.hashChangeListener = undefined;
    }
  }

  /**
   * Setup prevention for page refresh/close
   */
  private setupBeforeUnloadPrevention(): void {
    this.beforeUnloadListener = (event: BeforeUnloadEvent) => {
      // Standard way to prevent page unload
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  /**
   * Remove beforeunload prevention
   */
  private removeBeforeUnloadPrevention(): void {
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = undefined;
    }
  }

  /**
   * Setup prevention for programmatic router navigation
   */
  private setupRouterNavigationPrevention(): void {
    // Subscribe to navigation events and prevent them
    this.navigationStartSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Check if navigation is from browser back/forward
        if (event.navigationTrigger === 'popstate') {
          // Immediately cancel by navigating back to current URL
          const targetUrl = event.url;
          
          // Only prevent if navigating away from current URL
          if (targetUrl !== this.currentUrl && !targetUrl.startsWith(this.currentUrl)) {
            // Cancel the navigation
            this.router.navigateByUrl(this.currentUrl, { 
              skipLocationChange: false,
              replaceUrl: true 
            }).catch(() => {
              // If navigation fails, use Location service
              this.location.replaceState(this.currentUrl);
            });
          }
        } else {
          // Update current URL for programmatic navigation (allow it)
          setTimeout(() => {
            this.currentUrl = this.location.path() || '/';
          }, 0);
        }
      }
    });
  }

  /**
   * Remove router navigation prevention
   */
  private removeRouterNavigationPrevention(): void {
    if (this.navigationStartSubscription) {
      this.navigationStartSubscription.unsubscribe();
      this.navigationStartSubscription = undefined;
    }
    if (this.navigationEndSubscription) {
      this.navigationEndSubscription.unsubscribe();
      this.navigationEndSubscription = undefined;
    }
  }
}
